// --------------------------------------------------------------
// Created On: 2026-11-02
// Author: Zachary Thomas
//
// Last Modified: 2026-11-02
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useId } from "react";
import { GAUGE_TYPE_TANK_ROUND } from "src/constants/systemControl";
import styles from "./SystemControlComponentGaugeTank.module.scss";

// Tank graphic, either round or square, that fills with water and has its value, minimum, and maximum overlaid.
export default function SystemControlComponentGaugeTank(props: Props): Component {
  // Colons in generated IDs are not valid inside of the url() reference of a mask.
  const maskId = `tank-mask-${useId().replace(/:/g, "")}`;
  const isRound = props.gaugeType === GAUGE_TYPE_TANK_ROUND;

  // Returns the custom properties that drive the fill level and the highlight color of the graphic.
  function getTankStyle(): React.CSSProperties {
    const tankStyle: { [key: string]: string | number } = { "--tank-fill-percent": props.fillPercent };
    if (props.highlightColor !== null) {
      tankStyle["--tank-water-color"] = props.highlightColor;
      tankStyle["--tank-tint-color"] = props.highlightColor;
    }
    return tankStyle as React.CSSProperties;
  }

  // Returns the class names for the graphic.
  function getTankClassName(): string {
    if (props.highlightColor !== null) {
      return `${styles.tank} ${styles.highlightedTank}`;
    } else {
      return styles.tank;
    }
  }

  // Returns the class names for the text overlay.
  function getOverlayClassName(): string {
    if (isRound) {
      return styles.overlay;
    } else {
      return `${styles.overlay} ${styles.squareOverlay}`;
    }
  }

  return (
    <div className={styles.tankRow} style={getTankStyle()}>
      <div className={styles.graphic}>
        <svg className={getTankClassName()} viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          {isRound && (
            <>
              <path
                className={styles.background}
                d="M148.063 157.679C179.018 143.401 200 115.759 200 84C200 37.6081 155.228 0 100 0C44.7715 0 0 37.6081 0 84C0 115.759 20.9819 143.401 51.9367 157.679L60.4409 162.078C69.3839 166.704 75 175.932 75 186V200H125V186C125 175.932 130.616 166.704 139.559 162.078L148.063 157.679Z"
              />
              <path
                className={styles.tint}
                d="M148.063 157.679C179.018 143.401 200 115.759 200 84C200 37.6081 155.228 0 100 0C44.7715 0 0 37.6081 0 84C0 115.759 20.9819 143.401 51.9367 157.679L60.4409 162.078C69.3839 166.704 75 175.932 75 186V200H125V186C125 175.932 130.616 166.704 139.559 162.078L148.063 157.679Z"
              />
            </>
          )}
          {!isRound && (
            <>
              <rect className={styles.background} width="200" height="200" />
              <rect className={styles.tint} width="200" height="200" />
            </>
          )}
          <mask
            id={maskId}
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="200"
            height="200"
          >
            {isRound && (
              <path
                d="M148.063 157.679C179.018 143.401 200 115.759 200 84C200 37.6081 155.228 0 100 0C44.7715 0 0 37.6081 0 84C0 115.759 20.9819 143.401 51.9367 157.679L60.4409 162.078C69.3839 166.704 75 175.932 75 186V200H125V186C125 175.932 130.616 166.704 139.559 162.078L148.063 157.679Z"
                fill="black"
              />
            )}
            {!isRound && <rect width="200" height="200" fill="black" />}
          </mask>
          <g mask={`url(#${maskId})`}>
            <path
              className={styles.water}
              d="M0 1.53844C0 1.53844 50 -1.92306 100 1.53844C150 4.99995 200 1.53844 200 1.53844C200 1.53844 250 -1.92306 300 1.53844C350 4.99995 400 1.53844 400 1.53844V205H0V1.53844Z"
            />
          </g>
          {isRound && (
            <path
              className={styles.outline}
              d="M147.854 157.225L147.844 157.23L147.834 157.235L139.329 161.634C130.22 166.346 124.5 175.745 124.5 186V199.5H75.5V186C75.5 175.745 69.7797 166.346 60.6706 161.634L52.1664 157.235L52.1564 157.23L52.1461 157.225C21.3276 143.01 0.5 115.522 0.5 84C0.5 37.9624 44.9624 0.5 100 0.5C155.038 0.5 199.5 37.9624 199.5 84C199.5 115.522 178.672 143.01 147.854 157.225Z"
            />
          )}
          {!isRound && <rect className={styles.outline} x="0.5" y="0.5" width="199" height="199" />}
        </svg>
        <div className={getOverlayClassName()}>
          <div data-test="system-control-gauge-value" className={styles.value}>
            {props.value === null && "N/A"}
            {props.value !== null && props.value}
            {props.value !== null && props.unitShortName !== null && (
              <span className={styles.units}>{props.unitShortName}</span>
            )}
          </div>
        </div>
      </div>
      <div className={styles.scale}>
        <span data-test="system-control-gauge-max">{props.max}</span>
        <span data-test="system-control-gauge-min">{props.min}</span>
      </div>
    </div>
  );
}

interface Props {
  gaugeType: string;
  value: string | null;
  unitShortName: string | null;
  fillPercent: number;
  highlightColor: string | null;
  min: number | string;
  max: number | string;
}
